import { CLASS_NAMES, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the FooterPie module
 */
export default class FooterPie extends MessageBus {
  /**
   * Constructor function of the FooterPie class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    this.element = element;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} FooterPie A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Cache DOM References
   *
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} FooterPie A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.pies = this.element.querySelectorAll(`.${CLASS_NAMES.FOOTER_PIE}`);
    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} FooterPie A reference to the current instance of the class
   * @chainable
   */
  enable() {
    this.pies.forEach((pie) => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            observer.unobserve(pie);
            this.animatePie(pie);
          }
        },
        {
          threshold: [1]
        }
      );

      observer.observe(pie);
    });

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    return this;
  }

  /**
   * Animate the pie chart
   * @param {HTMLElement} pie - The pie chart element to animate
   * @returns {object} FooterPie A reference to the current instance of the class
   */
  animatePie(pie) {
    const targetPct = parseInt(pie.getAttribute(SELECTORS.DATA_TARGET_PCT), 10);
    const durationFast = 300; // Faster part (0-80%)
    const durationSlow = 875; // Slower part (81-100%)
    const startTime = performance.now();

    const arc = pie.querySelector(
      `${SELECTORS.SVG} ${SELECTORS.CIRCLE}:nth-child(2)`
    );
    if (!arc) return this;

    const radius = arc.getAttribute(SELECTORS.RADIUS);
    const circumference = 2 * Math.PI * radius;

    const fastPhasePct = 0.95 * targetPct; // 80% of the target
    let inSlowPhase = false;
    let slowPhaseStartTime = null;

    const updatePie = (currentTime) => {
      const elapsedTime = currentTime - startTime;

      if (!inSlowPhase) {
        const progressFast = Math.min(elapsedTime / durationFast, 1);
        const currentPct = Math.floor(progressFast * fastPhasePct);

        pie.setAttribute(SELECTORS.DATA_PCT, `${currentPct}%`);

        const strokeLength = (circumference / 100) * currentPct;
        arc.setAttribute(
          SELECTORS.STROKE_DASHARRAY,
          `${strokeLength} ${circumference - strokeLength}`
        );

        if (progressFast >= 1) {
          inSlowPhase = true;
          slowPhaseStartTime = performance.now();
        }
      }

      if (inSlowPhase) {
        const elapsedSlow = currentTime - slowPhaseStartTime;
        const progressSlow = Math.min(elapsedSlow / durationSlow, 1);
        const currentPct = Math.floor(
          fastPhasePct + progressSlow * (targetPct - fastPhasePct)
        );

        pie.setAttribute(SELECTORS.DATA_PCT, `${currentPct}%`);

        const strokeLength = (circumference / 100) * currentPct;
        arc.setAttribute(
          SELECTORS.STROKE_DASHARRAY,
          `${strokeLength} ${circumference - strokeLength}`
        );
      }

      const completed =
        inSlowPhase && currentTime - slowPhaseStartTime >= durationSlow;

      if (!completed) {
        requestAnimationFrame(updatePie);
      }
    };

    requestAnimationFrame(updatePie);

    return this;
  }
}
